import { supabase } from './supabase';
import { addDaysIso, daysBetweenIso } from '../components/shared/helpers';

// The boarding-point notice strip: everything that decides WHETHER a notice
// shows and WHAT it says, kept apart from the component so the rules can be
// tested against a date without rendering anything.
//
// Source: boarding_point_changes — the same rows boarding_point_on() applies
// to tickets and booking screens. Nothing here is typed in: the new point,
// the map link and the date all come from the row. A future change shows
// again automatically; the notice removes itself after the tail below.
//
// Timeline, relative to IST today (effective_from − today, in days):
//   +1 … +LEAD_DAYS   "Boarding point changes from 18 October: …"
//   −TAIL_DAYS … 0    "New boarding point: …"
//   anything else     nothing at all
export const LEAD_DAYS = 21;
export const TAIL_DAYS = 7;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// "2026-10-18" → "18 October". The only formatting the notice does; the
// boarding point itself is printed exactly as stored.
export function formatDayMonth(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!m) return '';
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month}` : '';
}

// One dismissal per change: the key carries the change's date, so a
// dismissed 18 October notice never hides a later change.
export function noticeStorageKey(effectiveFrom) {
  return `ag_boarding_notice_${effectiveFrom}`;
}

// The effective_from range a notice can be shown for, around IST today.
export function noticeWindow(todayIso) {
  return { from: addDaysIso(todayIso, -TAIL_DAYS), to: addDaysIso(todayIso, LEAD_DAYS) };
}

// 'upcoming' | 'active' | null for a change dated `effectiveFrom`, as of `todayIso`.
export function noticeState(effectiveFrom, todayIso) {
  const diff = daysBetweenIso(todayIso, effectiveFrom);
  if (diff === null) return null;
  if (diff > 0 && diff <= LEAD_DAYS) return 'upcoming';
  if (diff <= 0 && diff >= -TAIL_DAYS) return 'active';
  return null;
}

// The words before the boarding point, for a state.
export function noticeLead(state, effectiveFrom) {
  if (state === 'upcoming') return `Boarding point changes from ${formatDayMonth(effectiveFrom)}:`;
  if (state === 'active') return 'New boarding point:';
  return '';
}

// The full line for a row as of `todayIso` ("<lead> <boarding_point>"), or
// null when nothing should show.
export function noticeText(row, todayIso) {
  if (!row || !row.boarding_point) return null;
  const state = noticeState(row.effective_from, todayIso);
  if (!state) return null;
  return `${noticeLead(state, row.effective_from)} ${row.boarding_point}`;
}

// The earliest non-cancelled change inside the window around `todayIso`:
// { effective_from, boarding_point, boarding_maps_url } or null. Never
// throws — a notice that cannot load is a notice that does not show.
export async function fetchBoardingNotice(todayIso) {
  const { from, to } = noticeWindow(todayIso);
  if (!from || !to) return null;
  try {
    const { data, error } = await supabase
      .from('boarding_point_changes')
      .select('effective_from, boarding_point, boarding_maps_url')
      .is('cancelled_at', null)
      .gte('effective_from', from)
      .lte('effective_from', to)
      .order('effective_from', { ascending: true })
      .limit(1);
    if (error || !Array.isArray(data) || !data[0]) return null;
    const row = data[0];
    if (!row.effective_from || !String(row.boarding_point || '').trim()) return null;
    return row;
  } catch {
    return null;
  }
}

// localStorage may be unavailable (private mode, blocked storage): a read
// that throws counts as "not dismissed", a write that throws is ignored.
export function readDismissed(key) {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function writeDismissed(key) {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    /* storage unavailable — the notice simply shows again next visit */
  }
}
